"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
// Soft pacing cue: once a recording passes this many seconds, the elapsed-time readout shifts to
// an amber tone so a rep can self-pace toward a ~15-minute conversation target without having to
// watch the clock closely.
const PACING_CUE_SECONDS = 720; // 12 minutes
const containerStyle = {
    padding: '16px',
    border: '1px solid #e0e0e0',
    borderRadius: '10px',
    background: '#fafafa',
    marginBottom: '18px',
};
const primaryButtonStyle = {
    width: '100%',
    padding: '16px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
};
const modeButtonStyle = (active) => ({
    flex: 1,
    padding: '12px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
    border: active ? '2px solid #667eea' : '1px solid #ddd',
    background: active ? '#eef0fe' : 'white',
    color: active ? '#4c51bf' : '#666',
});
// Keeps in-app video recordings a manageable size for uploading over mobile data - a 15-minute
// demo at these settings lands well under 150MB, vs. a phone camera's default bitrate which could
// be several times that.
const VIDEO_CONSTRAINTS = { width: { ideal: 1280 }, height: { ideal: 720 } };
const VIDEO_BITS_PER_SECOND = 1500000;
const defaultAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : null;
};
const ConversationRecorder = ({ leadId, onTranscribed, apiBaseUrl, publicBaseUrl, onStatusChange, authHeaders = defaultAuthHeaders, uploadUrl, buildTranscribeUrl, }) => {
    const [mode, setMode] = (0, react_1.useState)('audio');
    const [status, setStatusState] = (0, react_1.useState)('idle');
    const setStatus = (next) => {
        setStatusState(next);
        onStatusChange?.(next);
    };
    const [elapsedSeconds, setElapsedSeconds] = (0, react_1.useState)(0);
    const [errorMessage, setErrorMessage] = (0, react_1.useState)(null);
    const [infoMessage, setInfoMessage] = (0, react_1.useState)(null);
    const [transcript, setTranscript] = (0, react_1.useState)(null);
    const [playbackUrl, setPlaybackUrl] = (0, react_1.useState)(null);
    const [playbackIsVideo, setPlaybackIsVideo] = (0, react_1.useState)(false);
    const mediaRecorderRef = (0, react_1.useRef)(null);
    const recordedChunksRef = (0, react_1.useRef)([]);
    const recordingIntervalRef = (0, react_1.useRef)(null);
    const recordingStartRef = (0, react_1.useRef)(0);
    const livePreviewRef = (0, react_1.useRef)(null);
    // Stop the ticking clock if the rep navigates away mid-recording.
    (0, react_1.useEffect)(() => {
        return () => {
            if (recordingIntervalRef.current)
                clearInterval(recordingIntervalRef.current);
        };
    }, []);
    const handleStartRecording = async () => {
        setErrorMessage(null);
        setInfoMessage(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia(mode === 'video' ? { audio: true, video: VIDEO_CONSTRAINTS } : { audio: true });
            if (mode === 'video' && livePreviewRef.current) {
                livePreviewRef.current.srcObject = stream;
            }
            const recorder = new MediaRecorder(stream, mode === 'video' ? { videoBitsPerSecond: VIDEO_BITS_PER_SECOND } : undefined);
            recordedChunksRef.current = [];
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0)
                    recordedChunksRef.current.push(e.data);
            };
            recorder.onstop = () => {
                stream.getTracks().forEach((t) => t.stop());
            };
            mediaRecorderRef.current = recorder;
            recorder.start();
            recordingStartRef.current = Date.now();
            setElapsedSeconds(0);
            recordingIntervalRef.current = setInterval(() => {
                setElapsedSeconds(Math.round((Date.now() - recordingStartRef.current) / 1000));
            }, 1000);
            setStatus('recording');
        }
        catch (error) {
            console.error('Error starting recording:', error);
            setErrorMessage(mode === 'video'
                ? "Couldn't access the camera/microphone - check your browser's permission for this site."
                : "Couldn't access the microphone - check your browser's permission for this site.");
            setStatus('error');
        }
    };
    const handleStopAndUpload = async () => {
        const recorder = mediaRecorderRef.current;
        // Checked on the native recorder, not the React `status` state - the visibility-change
        // auto-save below calls this from a listener set up once on mount, so `status` in that
        // closure would otherwise be stale.
        if (!recorder || recorder.state !== 'recording')
            return;
        if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
            recordingIntervalRef.current = null;
        }
        const blob = await new Promise((resolve) => {
            recorder.addEventListener('stop', () => resolve(new Blob(recordedChunksRef.current, { type: recorder.mimeType || (mode === 'video' ? 'video/webm' : 'audio/webm') })), { once: true });
            recorder.stop();
        });
        mediaRecorderRef.current = null;
        if (livePreviewRef.current)
            livePreviewRef.current.srcObject = null;
        // Save a copy to the device before uploading, so the rep keeps their own copy of the visit
        // regardless of what happens to the upload (network drops, etc.). Browser behavior varies by
        // device (Android typically saves straight to Downloads; iOS Safari usually prompts "Save to
        // Files" or opens a share sheet) - there's no way to force one exact behavior across phones.
        try {
            const ext = blob.type.includes('mp4') ? (mode === 'video' ? 'mp4' : 'm4a') : 'webm';
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = `sales-visit-${Date.now()}.${ext}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
        }
        catch (saveError) {
            console.error('Error saving local copy:', saveError);
            // Not fatal - continue to upload even if the local save didn't work on this device/browser.
        }
        setStatus('uploading');
        setErrorMessage(null);
        setInfoMessage(null);
        try {
            const headers = authHeaders();
            if (!headers) {
                setErrorMessage('You are not authenticated - the recording could not be uploaded.');
                setStatus('error');
                return;
            }
            const isVideo = blob.type.startsWith('video/');
            const ext = blob.type.includes('mp4') ? (isVideo ? 'mp4' : 'm4a') : 'webm';
            const file = new File([blob], `sales-visit-${Date.now()}.${ext}`, { type: blob.type || (isVideo ? 'video/webm' : 'audio/webm') });
            const formData = new FormData();
            formData.append('media', file);
            const uploadResponse = await fetch(uploadUrl || `${apiBaseUrl}/crm/leads/${leadId}/activities/media`, {
                method: 'POST',
                headers,
                body: formData,
            });
            const uploadData = await uploadResponse.json().catch(() => ({}));
            if (!uploadResponse.ok) {
                setErrorMessage(uploadData.error || 'Failed to upload the recording. Please try again.');
                setStatus('error');
                return;
            }
            const activityId = uploadData.activity?.id;
            const recordingUrl = uploadData.recordingUrl;
            if (recordingUrl) {
                setPlaybackUrl(`${publicBaseUrl}${recordingUrl}`);
                setPlaybackIsVideo(isVideo);
            }
            if (!activityId) {
                setErrorMessage('Recording saved, but no activity id was returned - transcription cannot be requested.');
                setStatus('error');
                return;
            }
            setStatus('transcribing');
            const transcribeResponse = await fetch(buildTranscribeUrl ? buildTranscribeUrl(activityId) : `${apiBaseUrl}/crm/activities/${activityId}/transcribe`, { method: 'POST', headers });
            const transcribeData = await transcribeResponse.json().catch(() => ({}));
            if (transcribeResponse.status === 503) {
                setInfoMessage("Transcription isn't set up yet - the recording was still saved.");
                setStatus('done');
                return;
            }
            if (transcribeResponse.status === 502 || transcribeResponse.status === 400) {
                setInfoMessage(transcribeData.error || 'Transcription failed - the recording was still saved.');
                setStatus('done');
                return;
            }
            if (!transcribeResponse.ok || !transcribeData.success) {
                setInfoMessage(transcribeData.error || 'Transcription failed - the recording was still saved.');
                setStatus('done');
                return;
            }
            const text = transcribeData.transcription || '';
            setTranscript(text);
            onTranscribed?.(text);
            setStatus('done');
        }
        catch (error) {
            console.error('Error uploading/transcribing recording:', error);
            setErrorMessage('Network error while uploading the recording. Please try again.');
            setStatus('error');
        }
    };
    // Mobile browsers (especially iOS Safari) can suspend or fully kill a backgrounded tab, wiping
    // the in-memory recording before the rep ever gets back to click "Stop & Upload" - this is what
    // happened switching to another app mid-recording on 2026-09-16. Auto-stop and upload the moment
    // the page is hidden so whatever was captured up to that point is saved instead of lost.
    (0, react_1.useEffect)(() => {
        const handleVisibilityChange = () => {
            if (document.hidden && mediaRecorderRef.current?.state === 'recording') {
                handleStopAndUpload();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const formattedElapsed = `${Math.floor(elapsedSeconds / 60)}:${String(elapsedSeconds % 60).padStart(2, '0')}`;
    const pastPacingCue = elapsedSeconds > PACING_CUE_SECONDS;
    return ((0, jsx_runtime_1.jsxs)("div", { style: containerStyle, children: [(status === 'idle' || status === 'error') && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)("div", { style: { display: 'flex', gap: '8px', marginBottom: '12px' }, children: [(0, jsx_runtime_1.jsx)("button", { type: "button", style: modeButtonStyle(mode === 'audio'), onClick: () => setMode('audio'), children: "\uD83C\uDF99\uFE0F Audio" }), (0, jsx_runtime_1.jsx)("button", { type: "button", style: modeButtonStyle(mode === 'video'), onClick: () => setMode('video'), children: "\uD83C\uDFA5 Video" })] }), mode === 'video' && ((0, jsx_runtime_1.jsx)("p", { style: { color: '#666', fontSize: '12px', marginTop: 0, marginBottom: '12px', lineHeight: 1.5 }, children: "Records your demo/presentation - great for rewatching to improve your pitch, or reusing on the next visit. AI qualification still works the same way either way." })), (0, jsx_runtime_1.jsx)("button", { type: "button", onClick: handleStartRecording, style: { ...primaryButtonStyle, background: '#667eea', color: 'white' }, children: mode === 'video' ? '🎥 Start Video Recording' : '🎙️ Start Recording' })] })), status === 'recording' && ((0, jsx_runtime_1.jsxs)("div", { children: [mode === 'video' && ((0, jsx_runtime_1.jsx)("video", { ref: livePreviewRef, autoPlay: true, muted: true, playsInline: true, style: { width: '100%', borderRadius: '8px', marginBottom: '12px', background: '#000' } })), (0, jsx_runtime_1.jsxs)("div", { style: {
                            textAlign: 'center',
                            fontSize: '20px',
                            fontWeight: 700,
                            color: pastPacingCue ? '#b45309' : '#333',
                            marginBottom: '12px',
                            transition: 'color 0.3s ease',
                        }, children: ["\u23FA\uFE0F ", formattedElapsed] }), (0, jsx_runtime_1.jsx)("button", { type: "button", onClick: handleStopAndUpload, style: { ...primaryButtonStyle, background: '#dc3545', color: 'white' }, children: "\u23F9\uFE0F Stop & Upload" })] })), status === 'uploading' && ((0, jsx_runtime_1.jsx)("div", { style: { textAlign: 'center', color: '#666', fontSize: '15px', fontWeight: 600, padding: '10px 0' }, children: "Uploading recording\u2026" })), status === 'transcribing' && ((0, jsx_runtime_1.jsx)("div", { style: { textAlign: 'center', color: '#666', fontSize: '15px', fontWeight: 600, padding: '10px 0' }, children: "Transcribing\u2026" })), status === 'done' && ((0, jsx_runtime_1.jsx)("div", { style: { textAlign: 'center', color: '#065f46', fontSize: '15px', fontWeight: 600, padding: '10px 0' }, children: "\u2705 Recording saved" })), errorMessage && ((0, jsx_runtime_1.jsx)("div", { style: {
                    marginTop: '12px',
                    padding: '10px 14px',
                    background: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    borderRadius: '8px',
                    color: '#721c24',
                    fontSize: '13px',
                }, children: errorMessage })), infoMessage && ((0, jsx_runtime_1.jsx)("div", { style: {
                    marginTop: '12px',
                    padding: '10px 14px',
                    background: '#fff3cd',
                    border: '1px solid #ffeeba',
                    borderRadius: '8px',
                    color: '#856404',
                    fontSize: '13px',
                }, children: infoMessage })), playbackUrl && (status === 'done' || status === 'error') && ((0, jsx_runtime_1.jsxs)("div", { style: { marginTop: '12px' }, children: [(0, jsx_runtime_1.jsx)("div", { style: { fontWeight: 700, fontSize: '13px', color: '#333', marginBottom: '6px' }, children: playbackIsVideo ? 'Watch back your presentation' : 'Play back recording' }), playbackIsVideo ? ((0, jsx_runtime_1.jsx)("video", { src: playbackUrl, controls: true, playsInline: true, style: { width: '100%', borderRadius: '8px' } })) : ((0, jsx_runtime_1.jsx)("audio", { src: playbackUrl, controls: true, style: { width: '100%' } }))] })), transcript && ((0, jsx_runtime_1.jsxs)("div", { style: {
                    marginTop: '12px',
                    padding: '12px 14px',
                    background: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                }, children: [(0, jsx_runtime_1.jsx)("div", { style: { fontWeight: 700, fontSize: '13px', color: '#333', marginBottom: '6px' }, children: "Transcript" }), (0, jsx_runtime_1.jsx)("div", { style: { fontSize: '14px', color: '#333', whiteSpace: 'pre-wrap', lineHeight: 1.5 }, children: transcript })] }))] }));
};
exports.default = ConversationRecorder;
